import React, { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit2, RefreshCw, Plus } from "lucide-react";
import { useMFOCodes } from "@/hooks/use-mfo-codes";
import { formatCurrency } from "@/utils/typeUtils";
import CreateMFOCodeModal from "@/components/mfo/CreateMFOCodeModal";
import EditMFOCodeModal from "@/components/mfo/EditMFOCodeModal";
import MFOTableActions from "@/components/mfo/MFOTableActions";

const MFOCodes = () => {
  const { mfoCodes, isLoading, error, fetchMFOCodes, createMFOCode, deleteMFOCode, updateMFOCode } = useMFOCodes();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMFO, setEditingMFO] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchMFOCodes();
    setIsRefreshing(false);
  };

  const handleEdit = (mfo: any) => {
    setEditingMFO(mfo);
  };

  const handleUpdate = async (data: any) => {
    await updateMFOCode(data);
    setEditingMFO(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMFOCode(id);
      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">MFO Codes Management</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New MFO Code
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>MFO Codes List</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">
              Failed to load MFO codes: {error}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mfoCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      No MFO codes found
                    </TableCell>
                  </TableRow>
                ) : (
                  mfoCodes.map((mfo) => (
                    <TableRow key={mfo.id}>
                      <TableCell className="font-medium">{mfo.code}</TableCell>
                      <TableCell>{mfo.title}</TableCell>
                      <TableCell>{formatCurrency(mfo.budget)}</TableCell>
                      <TableCell>
                        {new Date(mfo.created_at || "").toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <MFOTableActions 
                          mfoId={mfo.id} 
                          mfoCode={mfo.code}
                          mfoTitle={mfo.title}
                          onDelete={handleDelete}
                          onEdit={() => handleEdit(mfo)}
                          type="code"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateMFOCodeModal
        isOpen={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSave={createMFOCode}
        mode="create"
      />

      <EditMFOCodeModal
        isOpen={!!editingMFO}
        onOpenChange={() => setEditingMFO(null)}
        onSave={handleUpdate}
        mfo={editingMFO}
      />
    </div>
  );
};

export default MFOCodes;
